/**
 * View types of the voice sample (ADR-0062): about half a minute of the subject's free speech,
 * recorded in a dialog and kept for its voiceprint. One dialog serves both entries — a subject
 * being enrolled and one already enrolled — through its phases: instructions, recording, review,
 * saving, and (where it uploads) processing and how that ended. A recording begins with a check
 * of the room; a stored sample ends with the server's verdict on its quality.
 *
 * Not here, because not built: comparing voices, naming the voices of a questioning, playing a
 * stored sample back.
 */
import type { PlaybackState } from '../questioning/questioning-types'
import type {
  MicrophoneProblem,
  RecordingAudioState,
  RecordingSaveProblem,
} from '../questioning/recording-types'
import type { SubjectSummary } from '../types'
import type { VoiceSampleQuality } from './voice-sample-quality'

/** Why no sample was recorded: the microphone's reasons, or a recording that came out empty. */
export type VoiceMicrophoneProblem = MicrophoneProblem | 'empty'

/**
 * How the speech of a sample is counted: `speech` from the microphone's level, only while a voice
 * is heard; `time` where no level ever arrives (the meter could not be opened), every second.
 */
export type VoiceCounting = 'speech' | 'time'

/**
 * What a recording in progress says of the voice: one is heard and counted, none is heard, or —
 * where no level arrives — time is counted instead.
 */
export type VoiceLiveCaption = 'speaking' | 'waiting' | 'time'

/**
 * What the check of the room found before the speech is counted: it is quiet, there is some noise
 * (the sample may be weaker), or it is too loud (the operator is asked to find a quieter place,
 * and may go on all the same).
 */
export type VoiceRoom = 'quiet' | 'noisy' | 'loud'

/**
 * What the caption asks of the subject while it records: the voice is too faint (closer to the
 * phone, or louder), or the sound clips (further from the phone).
 */
export type VoiceGuidance = 'faint' | 'clipping'

/** What a screen reader is told while it records: of the voice, or what to ask of it. */
export type VoiceAnnounced = VoiceLiveCaption | VoiceGuidance

/** What moves while a sample records, several times a second. */
export interface VoiceReadout {
  /** Recorded time, from the moment the microphone opened: the room check is in it. */
  elapsedMs: number
  /** For how long the speech has been counted: the recorded time after the room check. */
  talkMs: number
  /** Net speech counted so far: what the ring shows. */
  speechMs: number
  /** A voice is heard now. */
  speaking: boolean
  counting: VoiceCounting
  /** The microphone's level now, 0..1. */
  level: number
  /** How far the check of the room has come, 0..1; null when none is under way. */
  roomShare: number | null
  /** What to ask of the subject now; null while the level is fine. */
  guidance: VoiceGuidance | null
  /**
   * What a screen reader was last told; null before anything was. It changes far less often than
   * `speaking` and `guidance`: a silence is told only once it has lasted, a voice only after a
   * silence that was told, and the same guidance not again for a while.
   */
  announced: VoiceAnnounced | null
}

/** Before anything is heard. */
export const IDLE_VOICE_READOUT: VoiceReadout = {
  elapsedMs: 0,
  talkMs: 0,
  speechMs: 0,
  speaking: false,
  counting: 'speech',
  level: 0,
  roomShare: null,
  guidance: null,
  announced: null,
}

/**
 * How a recording ended: it held all the speech asked for, it reached its longest length, the
 * operator stopped it, or the microphone went away (a call, a revoked permission).
 */
export type VoiceTakeEnding = 'target' | 'cap' | 'stopped' | 'interrupted'

/** What the screens show of a recorded sample. */
export interface VoiceTakeSummary {
  fileName?: string
  /** Recorded time. */
  durationMs: number
  /** Net speech counted in it. */
  speechMs: number
  counting: VoiceCounting
  ending: VoiceTakeEnding
  /** The level over the recording, 0..1 per bar, for the review's player. */
  waveform: readonly number[]
}

/** Why a sample is not saved yet: what a recording's upload says, without its language. */
export type VoiceSaveProblem = Exclude<RecordingSaveProblem, 'language'>

/** Why a sample ended without a voiceprint, where trying again may help. */
export type VoiceSampleFailure =
  /** The upload failed. */
  | { kind: 'send'; problem: VoiceSaveProblem }
  /** The sample is stored and its job failed: sending the same sample again runs it again. */
  | { kind: 'processing' }

/** The dialog's phase, with what each one shows. */
export type VoiceSampleView =
  | {
      phase: 'instructions'
      /** The browser is asking for the microphone. */
      starting: boolean
      problem: VoiceMicrophoneProblem | null
    }
  /**
   * The microphone is open and the recording runs; the speech is not counted yet. `measuring`:
   * the room is listened to, for about two seconds. `loud`: it is too loud here — the operator
   * checks again elsewhere, or goes on all the same.
   */
  | { phase: 'room'; check: 'measuring' | 'loud'; wakeLock: 'held' | 'unsupported' }
  /**
   * `room`: what the check found; null where it could not tell, absent where none was made (the
   * browser gives no true level).
   */
  | { phase: 'recording'; wakeLock: 'held' | 'unsupported'; room?: VoiceRoom | null }
  | { phase: 'review'; take: VoiceTakeSummary }
  /** `progress` 0..1 of the bytes sent; null while the sample is being prepared. */
  | { phase: 'saving'; take: VoiceTakeSummary; progress: number | null }
  /** Stored; the voiceprint is being computed. The dialog may be closed. */
  | { phase: 'processing'; take: VoiceTakeSummary }
  /**
   * The voiceprint is kept. `quality`: the server's verdict on the sample, once the page knows
   * it; null or absent where it says none.
   */
  | { phase: 'done'; take: VoiceTakeSummary; quality?: VoiceSampleQuality | null }
  /** The server found too little speech in it for a voiceprint. */
  | { phase: 'no_speech'; take: VoiceTakeSummary }
  | { phase: 'failed'; take: VoiceTakeSummary; failure: VoiceSampleFailure }

export type VoiceSamplePhase = VoiceSampleView['phase']

/**
 * What the dialog does with a sample the operator keeps: `collect` hands it back and sends
 * nothing (no subject exists yet); `upload` stores it with the subject.
 */
export type VoiceSampleMode = 'collect' | 'upload'

/** What the dialog was opened for. */
export interface VoiceSampleContext {
  mode: VoiceSampleMode
  /** Whose voice it is; none before the subject exists. */
  subject?: VoiceSubject | null
  /**
   * `collect` only: the subject exists already, so whoever opened the dialog sends the sample the
   * moment it is handed over. The review then says that, not *saved once the subject is created*.
   */
  sendsAtOnce?: boolean
}

/** The review's player on the recorded sample. */
export interface VoicePlayer {
  audio: RecordingAudioState
  playback: PlaybackState
  actions: { onTogglePlay: () => void; onSeek: (ms: number) => void }
}

/** Whose voice it is, as the header names them. */
export type VoiceSubject = Pick<
  SubjectSummary,
  'captureTag' | 'subjectNo' | 'givenName' | 'surname' | 'sex' | 'enrolledAt'
>

/**
 * What the operator can do. A missing callback hides its control: a host that cannot record
 * here, or may not save, simply does not pass it.
 */
export interface VoiceSampleActions {
  onImportFile?: (
    file: File
  ) => import('../questioning/recording-types').RecordingFileProblem | null
  /** The header's close and every *Close* / *Done*; the host asks first when a sample would be lost. */
  onClose: () => void
  /** Open the microphone and record. Called inside the tap. */
  onStart?: () => void
  /** End the recording early. */
  onStop?: () => void
  /**
   * Drop what is being recorded and record from zero — the room is checked again. Called inside
   * the tap.
   */
  onStartOver?: () => void
  /** Count the speech although the room is too loud. */
  onGoOn?: () => void
  /** Drop the recorded sample and record another. Called inside the tap. */
  onRecordAgain?: () => void
  /** Keep the sample: store it with the subject, or hand it back (`collect`). */
  onSave?: () => void
  /** Send the same sample again, under the id it already has. */
  onRetry?: () => void
}
